import { atom, computed, map, MapStore, ReadableAtom, WritableAtom } from 'nanostores';
import {
  ARCHITECTURAL_DIAGRAM_FILE_NAME,
  TRANSFORMATION_MAPPING_HTML_FILE_NAME,
  TRANSFORMATION_MAPPING_MD_FILE_NAME,
  VERSORI_FLOW_FILE_NAME,
} from '~/utils/constants';
import { computeFileModifications } from '~/utils/diff';
import { ScrollPosition } from '~/components/CodeMirror/CodeMirror';

/*
'workbenchFiles' - these are the saved versions of files for the current project
'files' - these are the current session versions of the files in the workbench.
When we save the files we resync the saved files with the workbench files
*/

export const emptyEditorDocument: EditorDocument = {
  content: '',
  type: 'file',
  isBinary: false,
  filePath: '',
};

export type EditorDocument = {
  content: string;
  type: 'file' | 'folder';
  isBinary: boolean;
  filePath: string;
  scroll?: ScrollPosition;
};

export type ProjectDocument = EditorDocument; // need to handle undefined file contents when deleting files

export type EditorDocuments = Record<string, ProjectDocument>;

function normalizeFilePath(path: string): string {
  if (!path) return path;
  return path.replace(/\\/g, '/');
}

function normalizeFiles(files: EditorDocuments): EditorDocuments {
  const out: EditorDocuments = {};
  for (const [key, doc] of Object.entries(files)) {
    const normalizedKey = normalizeFilePath(key);
    out[normalizedKey] = doc.filePath === normalizedKey ? doc : { ...doc, filePath: normalizedKey };
  }
  return out;
}

type SelectedFile = WritableAtom<string | undefined>;

export class WorkbenchStore {
  /* current saved directory in the DB for the project */
  workbenchFiles: MapStore<EditorDocuments> = map({});

  /* files for the current session */
  files: MapStore<EditorDocuments> = map({});

  /* currently selected file */
  selectedFile: SelectedFile = atom<string | undefined>();

  /* list of file names that have unsaved changes */
  unsavedFiles: WritableAtom<Set<string>> = atom(new Set<string>());

  /* the currently modified files and their modified contents */
  modifiedFiles: MapStore<Record<string, string>> = map({});

  /* contains the currently modified files that have been modified by the AI */
  aiModifiedFiles: MapStore<Record<string, boolean>> = map({});

  /* list of file paths that have been deleted but not yet saved */
  deletedFiles: WritableAtom<Set<string>> = atom(new Set<string>());

  /* subset of deletedFiles whose removal was caused by a rename (old path of a renamed file).
     Tracked separately so the UI can distinguish a rename from a true deletion. */
  renamedFiles: WritableAtom<Set<string>> = atom(new Set<string>());

  /* Project whose local draft has already been restored. File resets (version
     switch, branch pull) must not clear this; the workspace reset does. */
  restoredDraftProjectId: string | null = null;

  /* returns number of files in the current directory */
  size = computed([this.files], (files) => {
    return Object.keys(files).length;
  });

  aiModifiedFilesExist = computed([this.aiModifiedFiles], (aiModifiedFiles) => {
    return Object.values(aiModifiedFiles).some((isModified) => isModified);
  });

  /* returns whether there are any deleted files pending save */
  hasDeletedFiles = computed([this.deletedFiles], (deletedFiles) => {
    return deletedFiles.size > 0;
  });

  /* returns whether every pending deletion is the result of a rename (vs a true delete),
     so the UI can show a "renamed" instead of "deleted" sync warning */
  allDeletionsAreRenames = computed([this.deletedFiles, this.renamedFiles], (deletedFiles, renamedFiles) => {
    if (deletedFiles.size === 0) return false;
    return Array.from(deletedFiles).every((filePath) => renamedFiles.has(filePath));
  });

  /* returns the currently selected file */
  currentDocument = computed([this.files, this.selectedFile], (files, selectedFile) => {
    if (!selectedFile) {
      const workflowFileKey = Object.keys(files).find((key) => key.startsWith('src/workflows/'));
      if (workflowFileKey) {
        this.selectFile(workflowFileKey);
        return files[workflowFileKey];
      }
      return undefined;
    }

    return files[selectedFile];
  });

  /* returns the versori flow file */
  versoriFlowJsonFile = computed([this.files], (files) => {
    if (!files) return undefined;

    const flowFile = files[`${VERSORI_FLOW_FILE_NAME}`];
    if (!flowFile) return undefined;

    return flowFile;
  });

  /* returns the architectural diagram file */
  architecturalDiagramFile: ReadableAtom<EditorDocument | undefined> = computed([this.files], (files) => {
    return Object.values(files).find((file) => file.filePath.endsWith(ARCHITECTURAL_DIAGRAM_FILE_NAME));
  });

  /* returns the transformation mapping file — prefers .html (v2 visualiser sub-agent
     output) and falls back to .md (v1 services flow output) so the Transformations tab
     can render either format and v2 → v1 / v1 → v2 transitions don't show stale state. */
  transformationMappingFile: ReadableAtom<EditorDocument | undefined> = computed([this.files], (files) => {
    const values = Object.values(files);
    return (
      values.find((file) => file.filePath.endsWith(TRANSFORMATION_MAPPING_HTML_FILE_NAME)) ??
      values.find((file) => file.filePath.endsWith(TRANSFORMATION_MAPPING_MD_FILE_NAME))
    );
  });

  /* returns the markdown content of the currently selected file if truthy */
  markdownFile: ReadableAtom<string | undefined> = computed([this.files, this.selectedFile], (files, selectedFile) => {
    if (!selectedFile) return undefined;
    const file = files[selectedFile];
    return file?.type === 'file' && file.filePath.endsWith('.md') ? file.content : undefined;
  });

  /* returns the currently selected file when it's an HTML document, so the Build
     editor can preview it in a sandboxed iframe. Returns the document rather than
     the content string (as `markdownFile` does) because `HtmlSandbox` needs a
     title as well, and so the "is this previewable" checks live in one place. */
  htmlFile: ReadableAtom<EditorDocument | undefined> = computed([this.files, this.selectedFile], (files, selectedFile) => {
    if (!selectedFile) return undefined;
    const file = files[selectedFile];
    if (!file || file.type !== 'file' || file.isBinary) return undefined;
    return file.filePath.toLowerCase().endsWith('.html') ? file : undefined;
  });

  /* returns the package.json file */
  packageJsonFile: ReadableAtom<EditorDocument | undefined> = computed([this.files], (files) => {
    return Object.values(files).find((file) => file.filePath.endsWith('package.json'));
  });

  /*
    returns the associated workbench file for the currently selected file.
    This is used to track unsaved changes for the currently selected file.
    If the file is not in the workbench, we return undefined.
  */
  associatedWorkbenchFile = computed([this.files, this.selectedFile], (files, selectedFile) => {
    if (!selectedFile || !Object.keys(files)?.length) {
      return undefined;
    }
    return this.getWorkbenchFile(selectedFile);
  });

  /*
    Returns whether the current file has uncommitted changes that can be diffed.
    True when the file is in unsavedFiles AND has an associated workbench file to compare against.
  */
  currentFileHasDiff = computed(
    [this.selectedFile, this.unsavedFiles, this.workbenchFiles],
    (selectedFile, unsavedFiles, workbenchFiles) => {
      if (!selectedFile) return false;
      return unsavedFiles.has(selectedFile) && !!workbenchFiles[selectedFile];
    }
  );

  /*
    Returns whether the current file was modified by AI.
  */
  isCurrentFileAiModified = computed([this.selectedFile, this.aiModifiedFiles], (selectedFile, aiModifiedFiles) => {
    if (!selectedFile) return false;
    return !!aiModifiedFiles[selectedFile];
  });

  /*
    Add a file to the session files.
    If the file was modified by the AI, we track it in the aiModifiedFiles value
    Add the file to the unsavedFiles
  */

  addFile({
    filePath,
    content,
    fromDB,
    trackAiModified,
  }: {
    filePath: string;
    content: string;
    fromDB: boolean;
    trackAiModified: boolean;
  }) {
    const normalizedPath = normalizeFilePath(filePath);
    this.files.setKey(`${normalizedPath}`, {
      content: content,
      filePath: normalizedPath,
      isBinary: false,
      type: 'file',
      scroll: {
        left: 0,
        top: 0,
      },
    });
    if (fromDB) {
      return;
    }

    // If this path was previously deleted/renamed-away, adding it back cancels that pending
    // deletion (e.g. renaming a file back to its original name).
      this.clearDeletionTracking(normalizedPath);

    // If the saved (DB) version of this file already has this exact content, this call is
    // just replaying already-persisted content — e.g. re-rendering historical chat messages
    // (research docs/workflows) on chat load — rather than introducing a real change. Don't
    // flag it as unsaved/AI-modified, or a previously-saved file would show as dirty again
    // every time the chat is reopened.
    const workbenchFile = this.getWorkbenchFile(normalizedPath);
    if (workbenchFile && workbenchFile.content === content) {
      return;
    }

      this.setAiModifiedFile(normalizedPath, trackAiModified);

    const previousUnsavedFiles = this.unsavedFiles.get();

    const newUnsavedFiles = new Set(previousUnsavedFiles);
    newUnsavedFiles.add(normalizedPath);
    this.unsavedFiles.set(newUnsavedFiles);
  }

  removeFile(filePath: string, options?: { renamed?: boolean }) {
    // Remove from session files (this hides the file from the UI)
    const files = this.files.get();
    const { [filePath]: _removedFile, ...remainingFiles } = files;
    this.files.set(remainingFiles);

    // Keep workbenchFiles intact - it represents the saved DB state
    // The diff between files and workbenchFiles will indicate this file needs to be deleted on save

    // Track as a deleted file pending save
    const deletedFiles = this.deletedFiles.get();
    const newDeletedFiles = new Set(deletedFiles);
    newDeletedFiles.add(filePath);
    this.deletedFiles.set(newDeletedFiles);

    // When the removal is part of a rename, also record it so the UI can present a
    // "renamed" (rather than "deleted") sync warning. Both point at the same pending
    // deletion of the old path on save.
    const renamedFiles = this.renamedFiles.get();
    const newRenamedFiles = new Set(renamedFiles);
    if (options?.renamed) {
      newRenamedFiles.add(filePath);
    } else {
      newRenamedFiles.delete(filePath);
    }
    this.renamedFiles.set(newRenamedFiles);

    // Remove from modified files
    const modifiedFiles = this.modifiedFiles.get();
    if (filePath in modifiedFiles) {
      const { [filePath]: _removedModified, ...remainingModifiedFiles } = modifiedFiles;
      this.modifiedFiles.set(remainingModifiedFiles);
    }

    // Remove from AI modified files
    const aiModifiedFiles = this.aiModifiedFiles.get();
    if (filePath in aiModifiedFiles) {
      const { [filePath]: _removedAiModified, ...remainingAiModifiedFiles } = aiModifiedFiles;
      this.aiModifiedFiles.set(remainingAiModifiedFiles);
    }

    // If the removed file was selected, clear selection or select another file
    const selectedFile = this.selectedFile.get();
    if (selectedFile === filePath) {
      const remainingFileKeys = Object.keys(remainingFiles);
      if (remainingFileKeys.length > 0) {
        this.selectFile(remainingFileKeys[0]);
      } else {
        this.selectFile(undefined);
      }
    }
  }

  /*
    Remove a path from the pending deletion/rename tracking sets.
    Used when a previously removed path becomes live again (e.g. renaming a file back).
  */
  clearDeletionTracking(filePath: string) {
    const deletedFiles = this.deletedFiles.get();
    if (deletedFiles.has(filePath)) {
      const newDeletedFiles = new Set(deletedFiles);
      newDeletedFiles.delete(filePath);
      this.deletedFiles.set(newDeletedFiles);
    }

    const renamedFiles = this.renamedFiles.get();
    if (renamedFiles.has(filePath)) {
      const newRenamedFiles = new Set(renamedFiles);
      newRenamedFiles.delete(filePath);
      this.renamedFiles.set(newRenamedFiles);
    }
  }

  /*
    Sync workbenchFiles (from DB) with files on session
  */
  syncFilesToSession(files: EditorDocuments) {
    this.files.set(normalizeFiles(files));
  }

  /*
    Add all files to the workbench files
  */
  addWorkbenchFiles({ files }: { files: EditorDocuments }) {
    const normalized = normalizeFiles(files);
    this.workbenchFiles.set(normalized);

    // Merge into the session rather than replacing it: a project refetch (any
    // query invalidation while the agent is streaming, or right after the
    // package.json-driven auto-rename) must not revert staged-but-unsaved
    // files to OneAPI's state, nor resurrect files pending deletion. Callers
    // that intend a true replace (version switch, git pull) reset the
    // unsaved/deleted tracking first, which makes this merge a no-op.
    const session = this.files.get();
    const merged = { ...normalized };
    for (const filePath of this.unsavedFiles.get()) {
      if (session[filePath]) {
        merged[filePath] = session[filePath];
      }
    }
    for (const filePath of this.deletedFiles.get()) {
      delete merged[filePath];
    }
    this.files.set(merged);
  }

  syncFileToSession(filePath: string, file: EditorDocument) {
    const normalizedPath = normalizeFilePath(filePath);
    const doc = file.filePath === normalizedPath ? file : { ...file, filePath: normalizedPath };
    this.files.setKey(normalizedPath, doc);
  }
  /*
    Add a single file to the workbench files
  */
  addWorkbenchFile(filePath: string, file: EditorDocument) {
    const normalizedPath = normalizeFilePath(filePath);
    const doc = file.filePath === normalizedPath ? file : { ...file, filePath: normalizedPath };
    this.workbenchFiles.setKey(normalizedPath, doc);
    this.syncFileToSession(normalizedPath, doc);
  }

  /*
    Get a single file from the workbench files
    Used to get the original content of the file for comparison with the current content
  */
  getWorkbenchFile(filePath: string) {
    return this.workbenchFiles.get()[filePath];
  }

  /* select a file to be the current file */
  selectFile(filePath: string | undefined) {
    this.selectedFile.set(filePath);
  }

  /*
    Get a single file from the session files
    Used to get the current content of the file for comparison with the original content
  */
  getFile(filePath: string) {
    return this.files.get()[filePath];
  }

  /*
    Update a single file in the session files
    Used to update the current content of the sessionfile
  */
  updateFile(filePath: string, newContent: string) {
    const normalizedPath = normalizeFilePath(filePath);
    const files = this.files.get();
    const fileState = files[`${normalizedPath}`];
    if (!fileState) {
      return;
    }

    // Skip update if content hasn't changed
    if (fileState.content === newContent) {
      return;
    }

    this.files.setKey(normalizedPath, {
      ...fileState,
      content: newContent,
    });
  }

  /*
    Set the modified file in the modifiedFiles store
    Used to track which files have been modified and the content of the modified file
  */
  setModifiedFile(filePath: string, content: string) {
    this.modifiedFiles.setKey(filePath, content);
  }

  /*
    Set the current document content
    Used to update the current content of the session file
  */
  setCurrentDocumentContent(newContent: string) {
    const filePath = this.currentDocument.get()?.filePath;

    if (!filePath) {
      return;
    }

    const workbenchFile = this.getWorkbenchFile(filePath);

    if (workbenchFile === undefined) {
      // New file not yet saved to DB — always update content and keep in unsavedFiles
      this.updateFile(filePath, newContent);
      const previousUnsavedFiles = this.unsavedFiles.get();
      const newUnsavedFiles = new Set(previousUnsavedFiles);
      newUnsavedFiles.add(filePath);
      this.unsavedFiles.set(newUnsavedFiles);
      return;
    }

    const originalContent = workbenchFile.content;
    const unsavedChanges = originalContent !== newContent;

    // Only update the file if there's an actual change
    if (unsavedChanges) {
      this.updateFile(filePath, newContent);
      this.setModifiedFile(filePath, originalContent);

      const previousUnsavedFiles = this.unsavedFiles.get();
      const newUnsavedFiles = new Set(previousUnsavedFiles);
      newUnsavedFiles.add(filePath);
      this.unsavedFiles.set(newUnsavedFiles);
    } else {
      // If there are no changes, remove from unsaved files
      const previousUnsavedFiles = this.unsavedFiles.get();
      if (previousUnsavedFiles.has(filePath)) {
        const newUnsavedFiles = new Set(previousUnsavedFiles);
        newUnsavedFiles.delete(filePath);
        this.unsavedFiles.set(newUnsavedFiles);
      }
    }
  }

  /*
    Set the ai modified file in the aiModifiedFiles store
    Used to track which files have been modified by the AI
  */
  setAiModifiedFile(filePath: string, isModified: boolean) {
    this.aiModifiedFiles.setKey(filePath, isModified);
  }

  /*
    Reset the ai modified file in the aiModifiedFiles store
    Used to reset the ai modified file to false when a user makes a change to the file
  */
  resetAiModifiedFile(filePath: string) {
    this.aiModifiedFiles.setKey(filePath, false);
  }

  /* returns file diff between files and modified files (documents) */
  getFileModifications() {
    if (Object.keys(this.files.get()).length === 0) {
      return undefined;
    }
    return computeFileModifications(this.files.get(), this.modifiedFiles);
  }

  /*
    Blanket reset of all changes
  */
  resetAllFiles() {
    this.unsavedFiles.set(new Set<string>());
    this.modifiedFiles.set({});
    this.aiModifiedFiles.set({});
  }

  handleResetUnsavedFiles(files: string[]) {
    const previousUnsavedFiles = this.unsavedFiles.get();
    const newUnsavedFiles = new Set(previousUnsavedFiles);
    files.forEach((filePath) => {
      newUnsavedFiles.delete(filePath);
    });
    this.unsavedFiles.set(newUnsavedFiles);
  }

  handleResetAiModifiedFiles(files: string[]) {
    const previousAiModifiedFiles = this.aiModifiedFiles.get();
    const newAiModifiedFiles = new Map<string, boolean>(Object.entries(previousAiModifiedFiles));
    files.forEach((filePath) => {
      newAiModifiedFiles.set(filePath, false);
    });
    this.aiModifiedFiles.set(Object.fromEntries(newAiModifiedFiles));
  }

  handleResetModifiedFiles(files: string[]) {
    const modifiedFiles = this.modifiedFiles.get();
    const newModifiedFiles = new Map<string, string>(Object.entries(modifiedFiles));
    files.forEach((filePath) => {
      newModifiedFiles.delete(filePath);
    });
    this.modifiedFiles.set(Object.fromEntries(newModifiedFiles));
  }
  /*
    Reset of selected files from all state tracking
  */
  resetFiles(files: string[]) {
    this.handleResetUnsavedFiles(files);
    this.handleResetAiModifiedFiles(files);
    this.handleResetModifiedFiles(files);
  }

  /*
    Clean up deleted files after a successful save.
    Clears the deletedFiles set and removes them from workbenchFiles since the deletion is now persisted.
  */
  cleanupDeletedFiles() {
    const deletedFiles = this.deletedFiles.get();

    if (deletedFiles.size === 0) {
      return;
    }

    // Remove deleted files from workbenchFiles (deletion is now persisted)
    const workbenchFiles = this.workbenchFiles.get();
    const newWorkbenchFiles = { ...workbenchFiles };
    deletedFiles.forEach((filePath) => {
      delete newWorkbenchFiles[filePath];
    });
    this.workbenchFiles.set(newWorkbenchFiles);

    // Clear the deletedFiles set (and the rename tracking that mirrors it)
    this.deletedFiles.set(new Set<string>());
    this.renamedFiles.set(new Set<string>());
  }

  /*
    Restore files from localStorage for a specific project
  */
  restoreFilesFromLocalStorage(projectId: string) {
    try {
      const savedFiles = localStorage.getItem(`unsavedFiles_${projectId}`);
      if (savedFiles) {
        const files = JSON.parse(savedFiles) as Record<string, EditorDocument>;

        // Add each file to the workbench
        Object.entries(files).forEach(([filePath, fileData]) => {
          this.addFile({
            filePath,
            content: fileData.content,
            fromDB: false,
            trackAiModified: false,
          });
        });

        // Clear the saved files from localStorage
        localStorage.removeItem(`unsavedFiles_${projectId}`);

        return true;
      }
    } catch (error) {
      console.error('Failed to restore files from localStorage:', error);
    }
    return false;
  }

  /*
    Check if there are saved files in localStorage for a specific project
  */
  hasSavedFilesInLocalStorage(projectId: string): boolean {
    try {
      const savedFiles = localStorage.getItem(`unsavedFiles_${projectId}`);
      return savedFiles !== null;
    } catch (error) {
      console.error('Failed to check saved files in localStorage:', error);
      return false;
    }
  }

  /*
    Get the count of saved files in localStorage for a specific project
  */
  getSavedFilesCount(projectId: string): number {
    try {
      const savedFiles = localStorage.getItem(`unsavedFiles_${projectId}`);
      if (savedFiles) {
        const files = JSON.parse(savedFiles);
        return Object.keys(files).length;
      }
    } catch (error) {
      console.error('Failed to get saved files count from localStorage:', error);
    }
    return 0;
  }

  /*
    Clear saved files from localStorage for a specific project
  */
  clearSavedFilesFromLocalStorage(projectId: string) {
    try {
      localStorage.removeItem(`unsavedFiles_${projectId}`);
    } catch (error) {
      console.error('Failed to clear saved files from localStorage:', error);
    }
  }

  /*
    Reset all state in the workbench store
  */

  reset() {
    this.workbenchFiles.set({});
    this.files.set({});
    this.selectedFile.set(undefined);
    this.unsavedFiles.set(new Set<string>());
    this.deletedFiles.set(new Set<string>());
    this.renamedFiles.set(new Set<string>());
    this.modifiedFiles.set({});
    this.aiModifiedFiles.set({});
  }
}

export const workbenchStore = new WorkbenchStore();
